import { useCallback, useMemo, useState } from "react";
import {
  BaggageInfoEnum,
  type BaggageInfoAllowed,
  OrderedExtraBagsEnum,
  type ExtraBags,
  type ItineraryBaggage,
  type Person,
  type Restrictions,
} from "@b2bportal/air-booking-api";
import { useI18nContext } from "@hopper-b2b/i18n";
import { ButtonWrap, Divider, MobilePopoverCard } from "@hopper-b2b/ui";
import clsx from "clsx";
import CarryOnIcon from "../../../assets/client/baggage-carry-on.svg?react";
import Baggage from "../../../assets/client/baggage-checked-bag.svg?react";
import PersonalItemIcon from "../../../assets/client/baggage-personal-item.svg?react";
import Chevron from "../../../assets/client/chevron.svg?react";
import Close from "../../../assets/client/close.svg?react";
import ExternalLink from "../../../assets/client/external-link.svg?react";
import { openExternalUrl } from "../../../utils/nubankHandoff";
import styles from "./styles.module.scss";

interface TripsFlightsDetailsBaggageProps {
  itineraryBaggage?: ItineraryBaggage;
  passengers: Person[];
  restrictions?: Restrictions;
  airlineHomePage?: string;
}

interface PassengerBagsInfo {
  checkedCount: number;
  carryOnCount: number;
  personalItemCount: number;
  totalCost: string;
  purchasedDescription: string;
  weight: string | null;
}

export const TripsFlightsDetailsBaggage = ({
  itineraryBaggage,
  passengers,
  restrictions,
  airlineHomePage,
}: TripsFlightsDetailsBaggageProps) => {
  const [showBagsPopover, setShowBagsPopover] = useState(false);
  const { t, formatFiatCurrency } = useI18nContext();

  const baggage = itineraryBaggage?.orderedExtraBags;

  const hasExtrabags =
    !!baggage &&
    Object.values(baggage.bagsPerPassenger || {}).some(
      ({ checkedBags = [], carryOnBags = [], personalItems = [] }) =>
        checkedBags.length > 0 ||
        carryOnBags.length > 0 ||
        personalItems.length > 0
    );

  const includedBags = useMemo(() => {
    const result = {
      // personalItem is always marked as available in flights-restrictions-bff
      // https://github.com/hopper-org/flights-restrictions-bff/blob/master/server/src/main/scala/com/hopper/flights/restrictions/bff/server/helpers/AmenitiesAndUtasHelpers.scala#L48
      personalItem: { count: 1 },
      carryOn: { count: 0 },
      checkedBag: { count: 0, weight: null as string | null },
    };
    if (restrictions?.baggageInfo?.BaggageInfo === BaggageInfoEnum.Allowed) {
      const a = (restrictions.baggageInfo as BaggageInfoAllowed).allowance;
      result.checkedBag.count = a.pieces;
      if (a.maximumMass)
        result.checkedBag.weight = `${a.maximumMass.value} ${a.maximumMass.unit}`;
    }
    if (
      restrictions?.carryOnBaggageInfo?.BaggageInfo === BaggageInfoEnum.Allowed
    ) {
      result.carryOn.count = (
        restrictions.carryOnBaggageInfo as BaggageInfoAllowed
      ).allowance.pieces;
    }
    return result;
  }, [restrictions]);

  const includedDescription = useMemo(() => {
    const parts: string[] = [];
    if (includedBags.personalItem.count > 0)
      parts.push(
        t("baggage.personalItemAllowance", {
          count: includedBags.personalItem.count,
        })
      );
    if (includedBags.carryOn.count > 0)
      parts.push(
        t("baggage.carryOnAllowance", { count: includedBags.carryOn.count })
      );
    if (includedBags.checkedBag.count > 0)
      parts.push(
        includedBags.checkedBag.weight
          ? t("baggage.countCheckedBagsWithWeight", {
              count: includedBags.checkedBag.count,
              weight: includedBags.checkedBag.weight,
            })
          : t("baggage.checkedBaggageAllowance", {
              count: includedBags.checkedBag.count,
            })
      );
    return parts.join(" · ");
  }, [includedBags, t]);

  const includedBagTypes = useMemo(
    () => ({
      personalItem: includedBags.personalItem.count > 0,
      carryOn: includedBags.carryOn.count > 0,
      checkedBag: includedBags.checkedBag.count > 0,
    }),
    [includedBags]
  );

  const getPassengerBags = useCallback(
    (paxId: string): ExtraBags | null => {
      if (
        baggage?.OrderedExtraBags === OrderedExtraBagsEnum.PerPassengerExtraBags
      ) {
        return baggage?.bagsPerPassenger[paxId] ?? null;
      }
      if (
        baggage?.OrderedExtraBags === OrderedExtraBagsEnum.AllPassengerExtraBags
      ) {
        return baggage?.bags ?? null;
      }
      return null;
    },
    [baggage]
  );

  const getPassengerBagsInfo = useCallback(
    (paxId: string): PassengerBagsInfo | null => {
      const passengerBags = getPassengerBags(paxId);
      if (!passengerBags) return null;

      const {
        checkedBags = [],
        carryOnBags = [],
        personalItems = [],
      } = passengerBags;

      if (
        checkedBags.length === 0 &&
        carryOnBags.length === 0 &&
        personalItems.length === 0
      ) {
        return null;
      }

      const weight =
        checkedBags.length > 0 && checkedBags[0].maximumMass
          ? `${checkedBags[0].maximumMass.value} ${checkedBags[0].maximumMass.unit}`
          : null;

      const allBags = [...checkedBags, ...carryOnBags, ...personalItems];
      const totalCostValue = allBags.reduce(
        (sum, bag) => sum + bag.price.fiat.value,
        0
      );
      const templatePrice = allBags[0]?.price.fiat;

      const parts: string[] = [];
      if (personalItems.length > 0)
        parts.push(
          t("baggage.personalItemAllowance", { count: personalItems.length })
        );
      if (carryOnBags.length > 0)
        parts.push(
          t("baggage.carryOnAllowance", { count: carryOnBags.length })
        );
      if (checkedBags.length > 0)
        parts.push(
          weight
            ? t("baggage.countCheckedBagsWithWeight", {
                count: checkedBags.length,
                weight,
              })
            : t("baggage.checkedBaggageAllowance", {
                count: checkedBags.length,
              })
        );

      return {
        checkedCount: checkedBags.length,
        carryOnCount: carryOnBags.length,
        personalItemCount: personalItems.length,
        totalCost: templatePrice
          ? formatFiatCurrency({ ...templatePrice, value: totalCostValue })
          : "",
        purchasedDescription: parts.join(" · "),
        weight,
      };
    },
    [formatFiatCurrency, getPassengerBags, t]
  );

  const showBagsPopoverHandler = useCallback(() => {
    setShowBagsPopover(!showBagsPopover);
  }, [showBagsPopover]);

  const grandTotal = useMemo(() => {
    if (!hasExtrabags) return "";

    const baggageType = baggage?.OrderedExtraBags;

    const collectAllBags = (bags: ExtraBags) => {
      const { checkedBags = [], carryOnBags = [], personalItems = [] } = bags;
      return [...checkedBags, ...carryOnBags, ...personalItems];
    };

    let allBags: ReturnType<typeof collectAllBags> = [];

    switch (baggageType) {
      case OrderedExtraBagsEnum.PerPassengerExtraBags:
        allBags = Object.values(baggage.bagsPerPassenger).flatMap(
          collectAllBags
        );
        break;
      case OrderedExtraBagsEnum.AllPassengerExtraBags:
        allBags = collectAllBags(baggage.bags);
        break;
    }

    const price = allBags[0]?.price.fiat ?? null;
    if (!price) return "";

    const totalValue = allBags.reduce(
      (sum, bag) => sum + bag.price.fiat.value,
      0
    );

    return formatFiatCurrency({
      ...price,
      value: totalValue,
    });
  }, [baggage, hasExtrabags, formatFiatCurrency]);

  const getBagIconClass = (isActive: boolean) =>
    clsx("bag-type-icon", { "bag-type-icon-inactive": !isActive });

  const renderBagIcons = (info?: PassengerBagsInfo | null) => {
    const personalActive =
      (info?.personalItemCount ?? 0) > 0 || includedBagTypes.personalItem;
    const carryOnActive =
      (info?.carryOnCount ?? 0) > 0 || includedBagTypes.carryOn;
    const checkedActive =
      (info?.checkedCount ?? 0) > 0 || includedBagTypes.checkedBag;

    return [
      <PersonalItemIcon
        key="personal"
        className={getBagIconClass(personalActive)}
      />,
      <CarryOnIcon key="carryon" className={getBagIconClass(carryOnActive)} />,
      <Baggage key="checked" className={getBagIconClass(checkedActive)} />,
    ];
  };

  return (
    <>
      <Divider className="mobile-trip-details-divider" />
      <ButtonWrap
        onClick={showBagsPopoverHandler}
        className={styles.TripsFlightsDetailsBaggage}
      >
        <div className="left">
          <Baggage className="bags-icon" />
          <div className="content">
            <p className="title">{t("baggage.title")}</p>
          </div>
        </div>
        <Chevron className="arrow-icon" />
      </ButtonWrap>

      <MobilePopoverCard
        open={showBagsPopover}
        className={styles.BaggageInfoPopover}
        onClose={showBagsPopoverHandler}
        centered={false}
        fullScreen
        contentClassName="baggage-info-popover-content"
        bottomButton={
          airlineHomePage ? (
            <button
              className="cta-button"
              onClick={() => openExternalUrl(airlineHomePage)}
            >
              {t("tripsDetails.accessAirlineWebsite")}
              <ExternalLink className="cta-icon" />
            </button>
          ) : undefined
        }
      >
        <>
          <ButtonWrap onClick={showBagsPopoverHandler}>
            <Close className="close" />
          </ButtonWrap>

          <h2 className="baggage-details-title">{t("baggage.title")}</h2>

          <div className="callout-box">
            <p className="callout-title">{t("baggage.calloutTitle")}</p>
            <p className="callout-description">
              {t("baggage.calloutDescription")}
            </p>
          </div>

          <div className="baggage-passengers">
            {passengers.map((passenger) => {
              const info = hasExtrabags
                ? getPassengerBagsInfo(passenger.id)
                : null;

              const hasPurchased =
                info &&
                (info.checkedCount > 0 ||
                  info.carryOnCount > 0 ||
                  info.personalItemCount > 0);

              return (
                <div
                  className="passenger-section"
                  key={`baggage-passenger-${passenger.id}`}
                >
                  <div className="passenger-name">
                    <p>
                      {passenger.givenName} {passenger.surname}
                    </p>
                  </div>

                  <div className="bag-category">
                    <p className="category-label">
                      {t("baggage.includedInFare")}
                    </p>
                    <p className="bag-description">{includedDescription}</p>
                  </div>

                  {hasPurchased && (
                    <div className="bag-category">
                      <p className="category-label">{t("baggage.purchased")}</p>
                      <div className="bag-row">
                        <p className="bag-description">
                          {info.purchasedDescription}
                        </p>
                        <p className="bag-cost">{info.totalCost}</p>
                      </div>
                    </div>
                  )}

                  <div className="bag-icons-row">{renderBagIcons(info)}</div>
                </div>
              );
            })}
          </div>

          {hasExtrabags && (
            <>
              <Divider className="total-divider" />

              <div className="total-row">
                <p className="total-label">{t("total")}</p>
                <p className="total-value">{grandTotal}</p>
              </div>
            </>
          )}
        </>
      </MobilePopoverCard>
    </>
  );
};
